import Divider from '@/components/primitives/Divider';
import { MobileSearchWidget } from '@/components/search-widget/MobileSearchWidget';
import { RegularSearchWidget } from '@/components/search-widget/RegularSearchWidget';
import {
  salesPointConfigurationSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import {
  outboundOrInboundJourneysLoadingSelector,
  purchaseFlowBookingCreationLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { purchaseFlowBookingIdSelector } from '@/features/purchase/purchaseSelectors';
import { useBackgroundTheme } from '@/hooks/useBackgroundTheme';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransAlert } from '@/i18n/trans/alert';
import { useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { getTenantBrand, isLightBackground } from '@/utils/color';
import { getTenantConfiguration } from '@/utils/tenant';
import { parseUrlSearchParams } from '@/utils/urlSearchParams';
import {
  SearchWidget,
  type FormattedValidationErrors,
} from '@packages/webshop-search-widget';
import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

export const SearchWidgetWrapper = () => {
  const { pathname } = useLocation();
  const tenant = useSelector(tenantSelector);
  const configuration = getTenantConfiguration(tenant.value);
  const { brandColors } = getTenantBrand(tenant);
  const { handleSubmit, currentSearchFormValues } = useSearchWidget();
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const purchaseBookingId = useSelector(purchaseFlowBookingIdSelector);
  const outboundOrInboundJourneysLoading = useSelector(
    outboundOrInboundJourneysLoadingSelector
  );
  const bookingLoading = useSelector(
    purchaseFlowBookingCreationLoadingSelector
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const backgroundTheme = useBackgroundTheme();

  useEffect(() => {
    if (salesPointConfiguration?.currencies?.length && !purchaseBookingId) {
      const {
        productType,
        origin,
        destination,
        departureDate,
        returnDate,
        passengers,
        promoCodes,
      } = parseUrlSearchParams(salesPointConfiguration.passengerTypes);

      if (
        origin?.value &&
        destination?.value &&
        departureDate &&
        passengers &&
        productType &&
        !isAutoSubmitted
      ) {
        handleSubmit({
          productType,
          origin,
          destination,
          departureDate,
          returnDate,
          passengers,
          promoCodes,
        });
      }

      setIsAutoSubmitted(true);
    }
  }, [
    handleSubmit,
    isAutoSubmitted,
    purchaseBookingId,
    salesPointConfiguration?.currencies?.length,
    salesPointConfiguration?.passengerTypes,
  ]);

  // Without this the widget only logs a failed validation to the console, so
  // a Search click that the form refuses looks to the customer like a dead
  // button — which is how BR-58550 reached support in the first place.
  const handleValidationError = useCallback(
    (errors: FormattedValidationErrors) => {
      const details = Object.entries(errors)
        .filter(([, error]) => error?.message)
        .map(([field, error]) => `${field}: ${error.message}`);
      toast.error(
        <div className="flex flex-col gap-1">
          <TransAlert i18nKey="searchFormValidationFailed" />
          {details.length > 0 && (
            <ul className="font-mono list-disc pl-4 text-xs">
              {details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );
    },
    []
  );

  const Component = isLaptopOrBigger ? RegularSearchWidget : MobileSearchWidget;

  return (
    <div className="sticky top-0 z-50 bg-background">
      <Component>
        <SearchWidget
          baseUrl={import.meta.env.VITE_APP_PROXY_TARGET}
          onSubmit={handleSubmit}
          onValidationError={handleValidationError}
          height="small"
          lockedFields={{
            submitButton: outboundOrInboundJourneysLoading || bookingLoading,
          }}
          brandColors={brandColors}
          backgroundTheme={
            isLaptopOrBigger
              ? isLightBackground(backgroundTheme)
                ? 'light'
                : 'dark'
              : 'light'
          }
          stopsSearchingStrategy={configuration?.stopsSearchingStrategy}
          formValues={currentSearchFormValues}
          submitOnPromoCodeChange={pathname !== '/purchase'}
        />
      </Component>
      <Divider variant="medium" />
    </div>
  );
};
